import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { getCalendarChartDataAction } from "../../Actions/TransactionActions";
import NoDataFound from "./NoDataFound";
import { MyCancelIcon } from "../../Assets/svg/MyIcons";
import {Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler);

const DashboardActivityGraph = ({
  selectedMerchant,
  selectedWebsite,
  selectedTimeRange,
  startDate,
  endDate,
  showButton,
  onRemove,
  onShowSnackbar,
}) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: (context) => {
          const value = context.dataset.data[context.dataIndex];
          if (value === 0) return "rgba(200, 200, 200, 0.2)";
          if (value <= 2) return "rgba(255, 183, 77, 0.5)";
          if (value <= 4) return "rgba(251, 140, 0, 0.8)";
          return "rgba(230, 81, 0, 1)";
        },
        borderWidth: 1,
        borderRadius: 2,
        borderColor: "transparent",
        barPercentage: 1.0,
        categoryPercentage: 1.0,
        barThickness: "flex",
      },
    ],
  });

  const [loading, setLoading] = useState(false);

  // Memoize chart options to prevent recreation
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: true, text: "Transaction Activity" },
    },
    scales: {
      x: { title: { display: true, text: "Date / Month" } },
      y: {
        title: {
          display: true,
          text: "Total Transaction Amount ($)",
        },
        beginAtZero: true,
        ticks: { stepSize: 1 },
      },
    },
  }), []);

  // Fetch chart data
  const fetchChartData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getCalendarChartDataAction({
        startDate: startDate,
        endDate: endDate,
        merchant: selectedMerchant,
        chartType: selectedTimeRange,
        websiteId: selectedWebsite || null,
      });

      if (response?.data?.labels && response?.data?.data) {
        setChartData({
          labels: response.data.labels,
          datasets: [
            {
              data: response.data.data,
              backgroundColor: (context) => {
                const value = context.dataset.data[context.dataIndex];
                if (value === 0) return "rgba(200, 200, 200, 0.2)";
                if (value <= 2) return "rgba(255, 183, 77, 0.5)";
                if (value <= 4) return "rgba(251, 140, 0, 0.8)";
                return "rgba(230, 81, 0, 1)";
              },
              borderWidth: 1,
              borderRadius: 2,
              borderColor: "transparent",
              barPercentage: 1.0,
              categoryPercentage: 1.0,
              barThickness: "flex",
            },
          ],
        });
      } else {
        setChartData({
          labels: [],
          datasets: [
            {
              data: [],
              backgroundColor: (context) => {
                const value = context.dataset.data[context.dataIndex];
                if (value === 0) return "rgba(200, 200, 200, 0.2)";
                if (value <= 2) return "rgba(255, 183, 77, 0.5)";
                if (value <= 4) return "rgba(251, 140, 0, 0.8)";
                return "rgba(230, 81, 0, 1)";
              },
              borderWidth: 1,
              borderRadius: 2,
              borderColor: "transparent",
              barPercentage: 1.0,
              categoryPercentage: 1.0,
              barThickness: "flex",
            },
          ],
        });
      }
    } catch (error) {
      setChartData({
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: (context) => {
              const value = context.dataset.data[context.dataIndex];
              if (value === 0) return "rgba(200, 200, 200, 0.2)";
              if (value <= 2) return "rgba(255, 183, 77, 0.5)";
              if (value <= 4) return "rgba(251, 140, 0, 0.8)";
              return "rgba(230, 81, 0, 1)";
            },
            borderWidth: 1,
            borderRadius: 2,
            borderColor: "transparent",
            barPercentage: 1.0,
            categoryPercentage: 1.0,
            barThickness: "flex",
          },
        ],
      });
      if (error?.response?.data?.message) {
        onShowSnackbar(error.response.data.message, "error");
      }
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedMerchant, selectedWebsite, selectedTimeRange, onShowSnackbar]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchChartData();
    }
  }, [startDate, endDate, selectedMerchant, selectedWebsite, fetchChartData]);

  return (
    <div className={`kpi-card ${!showButton && "my-3"}`} key="transactionActivityChart">
      {showButton && (
        <MyCancelIcon
          className="close-icon position-absolute cursor-pointer dash-close z-index-3"
          style={{ top: 5, right: 5 }}
          onClick={() => onRemove("transactionActivityChart")}
        />
      )}

      <div className="kpi-card-content">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="chart-title m-0">Total Activity</h2>
        </div>

        <div className="chart-canvas-container" style={{ height: "350px" }}>
          {loading ? (
            <div className="chart-loading">
              <CircularProgress />
              <p>Loading calendar data...</p>
            </div>
          ) : chartData.datasets[0].data.length === 0 || chartData.datasets[0].data.every((value) => value === 0) ? (
            <NoDataFound message="No Transaction Activity data available" />
          ) : (
            <Bar data={chartData} options={chartOptions} />
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardActivityGraph;